'use client'

import * as React from 'react'
import { Typography } from '@repo/design-system-cf/components/typography'
import { Breadcrumbs } from '@repo/web-ui/components/breadcrumbs'
import { useTranslations } from 'next-intl'
import { Link } from '@repo/web-ui/components/link'
import type { Locales } from 'cf-i18n/helpers'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import Image from 'next/image'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { MultiContentAndCategoriesSearchInput } from '@web/components/algolia/components/search-input/deferred-input'
import type { FontsHeaderCategory } from './marketplace'

export function Header({
  categories,
  locale,
  count,
  hideSearch = false,
  page,
}: {
  categories: FontsHeaderCategory[]
  locale: Locales
  count: number
  hideSearch?: boolean
  page?: number
}) {
  const t = useTranslations('pages.fonts.landing_page.header')
  const tSearch = useTranslations('pages.fonts.landing_page.search')

  const categorySelectFilters = categories
    .filter(category => !category.slug.includes('/tag/'))
    .map(category => category.name)

  const getCategoryName = (name: string) => {
    const fonts = t('fonts')
    if (!name.includes(fonts)) {
      return `${name} ${fonts}`
    }
    return name
  }

  return (
    <div className="flex flex-col relative z-[1]">
      <Breadcrumbs
        items={[
          {
            title: t('breadcrumbs.home'),
            path: '/',
          },
          {
            title: t('breadcrumbs.fonts'),
            path: page ? AppRoutes.FONTS_RELATIVE_PATH : undefined,
          },
          ...(page
            ? [
                {
                  title: t('breadcrumbs.page', { number: page }),
                },
              ]
            : []),
        ]}
        separator="/"
      />
      <div className="flex flex-col items-center mx-auto mt-[20px]">
        <Typography variant="cf-title-08" asChild className="text-center">
          <h1>{t('title')}</h1>
        </Typography>
        <Typography variant="body-02" className="text-center mt-4 max-w-[664px]">
          {t('description', { count: count.toLocaleString() })}
        </Typography>
        <div className="hidden md:grid grid-cols-6 gap-x-4 gap-y-6 mt-10">
          {categories.map(category => (
            <Link
              className="flex flex-col justify-between bg-cf-white rounded shadow-sm group"
              key={category.slug}
              href={addOrReplaceLocaleInUrl(category.slug, locale)}
            >
              {category.imageUrl ? (
                <Image
                  className="aspect-[3/2] object-cover rounded-t"
                  src={category.imageUrl}
                  alt={category.name}
                  width={200}
                  height={200}
                />
              ) : (
                <div />
              )}
              <Typography variant="cf-title-01" asChild>
                <h3
                  className="px-2.5 py-1.5 group-hover:text-cf-theme-green-700"
                  dangerouslySetInnerHTML={{ __html: getCategoryName(category.name) }}
                />
              </Typography>
            </Link>
          ))}
        </div>
        {!hideSearch ? (
          <div
            id="search-section"
            className="shadow-md mt-10 md:mt-[88px] max-w-[800px] w-full bg-cf-white"
          >
            <MultiContentAndCategoriesSearchInput
              href={AppRoutes.SEARCH}
              enableAutocomplete={true}
              filters="type:Fonts"
              placeholder={tSearch('placeholder')}
              no_results_text={tSearch('no_results')}
              searchSource="category-page"
              categorySelectFilters={categorySelectFilters}
              allCategoriesText={categorySelectFilters.length > 0 ? tSearch('all_categories') : ''}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
